import React, { useState, useEffect } from 'react';
import {
    TextField,
    Button,
    Box,
    FormLabel,
    InputAdornment,
    IconButton,
    Alert,
    Fade
} from '@mui/material';
import { Visibility, VisibilityOff, Key } from '@mui/icons-material';
import { calculatePasswordStrength, PasswordRequirements, PasswordStrengthIndicator } from '../utils/PasswordStrength';

export default function ChangePassword({ onChangePassword, onCancel }) {
    const [pw, setPw] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [showPasswords, setShowPasswords] = useState({
        oldPassword: false,
        newPassword: false,
        confirmPassword: false
    });

    const [errors, setErrors] = useState({
        newPassword: false,
        confirmPassword: false
    });

    const [errorMessages, setErrorMessages] = useState({
        newPassword: '',
        confirmPassword: ''
    });

    // Password strength state
    const [passwordStrength, setPasswordStrength] = useState({
        score: 0,
        level: 'none',
        color: '#gray',
        text: 'Enter password',
        timeToHack: '',
        requirements: {
            length: false,
            uppercase: false,
            lowercase: false,
            number: false
        }
    });

    const handleChange = (e) => {
        setPw({ ...pw, [e.target.name]: e.target.value });
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords({
            ...showPasswords,
            [field]: !showPasswords[field]
        });
    };

    // Validate new password whenever it changes
    useEffect(() => {
        if (pw.newPassword) {
            const strength = calculatePasswordStrength(pw.newPassword);
            setPasswordStrength(strength);

            // Validate password strength
            if (strength.score < 3) {
                setErrors(prev => ({ ...prev, newPassword: true }));
                setErrorMessages(prev => ({
                    ...prev,
                    newPassword: 'Password is too weak. Please choose a stronger password.'
                }));
            } else {
                setErrors(prev => ({ ...prev, newPassword: false }));
                setErrorMessages(prev => ({ ...prev, newPassword: '' }));
            }
        } else {
            setPasswordStrength({
                score: 0,
                level: 'none',
                color: '#gray',
                text: 'Enter password',
                timeToHack: '',
                requirements: {
                    length: false,
                    uppercase: false,
                    lowercase: false,
                    number: false
                }
            });
            setErrors(prev => ({ ...prev, newPassword: false }));
            setErrorMessages(prev => ({ ...prev, newPassword: '' }));
        }
    }, [pw.newPassword]);

    // Validate confirm password whenever it changes
    useEffect(() => {
        if (pw.confirmPassword) {
            if (pw.newPassword !== pw.confirmPassword) {
                setErrors(prev => ({ ...prev, confirmPassword: true }));
                setErrorMessages(prev => ({
                    ...prev,
                    confirmPassword: 'Passwords do not match.'
                }));
            } else {
                setErrors(prev => ({ ...prev, confirmPassword: false }));
                setErrorMessages(prev => ({ ...prev, confirmPassword: '' }));
            }
        } else {
            setErrors(prev => ({ ...prev, confirmPassword: false }));
            setErrorMessages(prev => ({ ...prev, confirmPassword: '' }));
        }
    }, [pw.newPassword, pw.confirmPassword]);

    const validateInputs = () => {
        let isValid = true;

        // Check if all fields are filled
        if (!pw.oldPassword || !pw.newPassword || !pw.confirmPassword) {
            return false;
        }

        // Check password strength
        if (passwordStrength.score < 3) {
            isValid = false;
        }

        // Check if passwords match
        if (pw.newPassword !== pw.confirmPassword) {
            isValid = false;
        }

        return isValid;
    };

    const handleSubmit = () => {
        if (!validateInputs()) {
            if (pw.newPassword !== pw.confirmPassword) {
                alert('New passwords do not match.');
            } else if (passwordStrength.score < 3) {
                alert('Password is too weak. Please choose a stronger password.');
            } else {
                alert('Please fill in all fields.');
            }
            return;
        }
        onChangePassword(pw);
    };

    return (
        <Box>
            {/* Old Password */}
            <Box sx={{ mb: 3 }}>
                <FormLabel htmlFor="oldPassword" sx={{ mb: 1, fontWeight: 500, display: 'block' }}>
                    Current Password
                </FormLabel>
                <TextField
                    id="oldPassword"
                    name="oldPassword"
                    type={showPasswords.oldPassword ? 'text' : 'password'}
                    value={pw.oldPassword}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Key />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => togglePasswordVisibility('oldPassword')}
                                    edge="end"
                                >
                                    {showPasswords.oldPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                        }
                    }}
                />
            </Box>

            {/* New Password */}
            <Box sx={{ mb: 3 }}>
                <FormLabel htmlFor="newPassword" sx={{ mb: 1, fontWeight: 500, display: 'block' }}>
                    New Password
                </FormLabel>
                <TextField
                    id="newPassword"
                    name="newPassword"
                    type={showPasswords.newPassword ? 'text' : 'password'}
                    value={pw.newPassword}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    error={errors.newPassword}
                    helperText={errorMessages.newPassword}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Key />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => togglePasswordVisibility('newPassword')}
                                    edge="end"
                                >
                                    {showPasswords.newPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                        }
                    }}
                />

                {/* Password strength indicator */}
                {pw.newPassword && (
                    <Fade in={!!pw.newPassword}>
                        <Box>
                            <PasswordStrengthIndicator strength={passwordStrength} />
                        </Box>
                    </Fade>
                )}

                {/* Password requirements */}
                <PasswordRequirements
                    requirements={passwordStrength.requirements}
                    password={pw.newPassword}
                />
            </Box>

            {/* Confirm Password */}
            <Box sx={{ mb: 3 }}>
                <FormLabel htmlFor="confirmPassword" sx={{ mb: 1, fontWeight: 500, display: 'block' }}>
                    Confirm New Password
                </FormLabel>
                <TextField
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPasswords.confirmPassword ? 'text' : 'password'}
                    value={pw.confirmPassword}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    error={errors.confirmPassword}
                    helperText={errorMessages.confirmPassword}
                    color={errors.confirmPassword ? 'error' : pw.confirmPassword && !errors.confirmPassword ? 'success' : 'primary'}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Key />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => togglePasswordVisibility('confirmPassword')}
                                    edge="end"
                                >
                                    {showPasswords.confirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                        }
                    }}
                />
            </Box>

            {/* Show warning if password is weak but user is trying to submit */}
            {pw.newPassword && passwordStrength.score < 3 && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
                    Your password is too weak. Please choose a stronger password to continue.
                </Alert>
            )}

            {/* Buttons */}
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!validateInputs()}
                    sx={{
                        borderRadius: '8px',
                        fontWeight: 600,
                        textTransform: 'none',
                        px: 3
                    }}
                >
                    Change Password
                </Button>
                <Button
                    variant="outlined"
                    color="error"
                    onClick={onCancel}
                    sx={{
                        borderRadius: '8px',
                        fontWeight: 600,
                        textTransform: 'none',
                        px: 3
                    }}
                >
                    Cancel
                </Button>
            </Box>
        </Box>
    );
}